import {
  BoldTextShortcut,
  BulletedListIcon,
  ItalicTextShortcut,
  NumberedListIcon,
  ThreeDotsIconShortcut,
  UnderlineTextShortcut,
} from 'icons';
import { useIntl } from 'localization';
import { match } from 'ts-pattern';

import { ShortCuts } from '../RichTextEditor';
import { AdditionalButton } from './AdditionalButton';
import { BlockButton } from './BlockButton';
import { InsertImageButton } from './InsertImageButton';
import { InsertLinkButton } from './InsertLinkButton';
import { MarkButton } from './MarkButton';
import MarkButtonEmojiPicker from './MarkButtonEmojiPicker';

const ShortcutButton = ({
  shortcut,
  title,
  hidden,
  onClose,
}: {
  shortcut: ShortCuts;
  title: string;
  hidden?: boolean;
  onClose?: () => void;
}) =>
  match(shortcut)
    .with('bold', () => (
      <MarkButton format="bold" title={title} icon={<BoldTextShortcut className="w-7 h-7" />} />
    ))
    .with('italic', () => (
      <MarkButton format="italic" title={title} icon={<ItalicTextShortcut className="w-7 h-7" />} />
    ))
    .with('underline', () => (
      <MarkButton
        format="underline"
        title={title}
        icon={<UnderlineTextShortcut className="w-7 h-7" />}
      />
    ))
    .with('link', () => <InsertLinkButton title={title} hidden={hidden} onClose={onClose} />)
    .with('emoji', () => <MarkButtonEmojiPicker title={title} hidden={hidden} onClose={onClose} />)
    .with('image', () => <InsertImageButton title={title} hidden={hidden} onClose={onClose} />)
    .with('numbered-list', () => (
      <BlockButton
        format="numbered-list"
        title={title}
        icon={<NumberedListIcon className="w-7 h-7" />}
      />
    ))
    .with('bulleted-list', () => (
      <BlockButton
        format="bulleted-list"
        title={title}
        icon={<BulletedListIcon className="w-7 h-7" />}
      />
    ))
    .exhaustive();

const ToolbarButtons = ({
  shortCuts,
  hidden,
  onClose,
}: {
  shortCuts?: ShortCuts[];
  hidden?: boolean;
  onClose?: () => void;
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      {shortCuts?.map((x, index) => (
        <ShortcutButton
          shortcut={x}
          title={formatMessage({ id: `desk-app.rich-editor.${x}` })}
          hidden={hidden}
          key={`toolbar-button-shortcut-${x}-${index}`}
          onClose={onClose}
        />
      ))}
    </>
  );
};

type ToolBarProps = {
  additionalToolbarButtons?: JSX.Element;
  visibleToolbarShortcuts?: ShortCuts[];
  hiddenToolbarShortcuts?: ShortCuts[];
  actionElement?: JSX.Element;
};

const ToolBar = ({
  additionalToolbarButtons,
  visibleToolbarShortcuts,
  hiddenToolbarShortcuts,
  actionElement,
}: ToolBarProps) => {
  return (
    <div className="mt-4 flex gap-2 justify-between items-end">
      <div className="flex gap-1 flex-wrap">
        <ToolbarButtons shortCuts={visibleToolbarShortcuts} />
        {additionalToolbarButtons}

        {!!hiddenToolbarShortcuts?.length && (
          <AdditionalButton
            icon={<ThreeDotsIconShortcut className="w-7 h-7" />}
            shorcuts={(onClose) => (
              <ToolbarButtons shortCuts={hiddenToolbarShortcuts} onClose={onClose} hidden />
            )}
          />
        )}
      </div>
      {actionElement}
    </div>
  );
};

export default ToolBar;
